import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import RoomBreakdownDetails from './RoomBreakdownDetails'
import RoomBreakdownLoadingSkeleton from './RoomBreakdownLoadingSkeleton'
import useOfferMetaData from '../../../../hooks/Offers/useOfferMetaData'
import { isBedbankOffer, isBedbankSearchRoom, isLEOffer, isReservationSearchRoom, isVillaOffer } from 'lib/offer/offerTypes'
import { getMaxNonInfantGuestCount } from 'lib/offer/occupancyUtils'
import { isAnytimeSearch, shouldHideSearchTileRoomBreakdown } from './searchTileRoomBreakdownUtils'
import useBedbankRoomBreakdown from '../../../../hooks/useBedbankRoomBreakdown'
import { getRoomBreakdown, mapSearchOccupancyToOccupants } from 'lib/search/searchUtils'
import { excludeNullOrUndefined } from 'checkout/utils'

interface Props {
  offer: App.HotelOffer | App.HotelOfferSummary | App.BedbankOffer | App.BedbankOfferSummary | App.VillaOffer | App.VillaOfferSummary
  filters?: App.OfferListFilters
}

function SearchTileRoomBreakdown({
  offer,
  filters,
}: Props) {
  const intl = useIntl()
  const metaData = useOfferMetaData(offer.id, filters)
  const isSingleUnit = isVillaOffer(offer) || offer.holidayTypes?.includes('Single Unit')

  const leRoomsList = useMemo(() => {
    const packages = isLEOffer(offer) ? offer.packages : []
    const metaDataRooms = metaData?.rooms?.filter(isReservationSearchRoom) ?? []
    return getRoomBreakdown(metaDataRooms, packages)
  }, [metaData?.rooms, offer])

  const bedbankRooms = useMemo(() =>
    metaData?.rooms
      ?.filter(isBedbankSearchRoom)
      .map(r => (r.rateId ?
          {
            rateId: r.rateId,
            propertyId: r.propertyId,
            occupancy: mapSearchOccupancyToOccupants(r.occupancy),
          }
        : undefined))
      .filter(excludeNullOrUndefined) ?? [],
  [metaData?.rooms])

  const { rooms: bedbankRoomsList, isLoading: isBedbankLoading } = useBedbankRoomBreakdown(bedbankRooms, filters)

  // For single-unit properties, derive capacity from rates if available, otherwise fall back to static package data (e.g. anytime searches where no rates are fetched)
  const entireHome = useMemo(() => {
    if (!isSingleUnit) return undefined

    const bedbankPackage = isBedbankOffer(offer) ? offer.packages[0] : undefined
    const bedbankTotalGuests = bedbankPackage?.capacities?.combinations?.length
      ? Math.max(...bedbankPackage.capacities.combinations.map(c => c.adults + c.children + c.teenagers))
      : undefined

    const villaPackage = isLEOffer(offer) ? offer.lowestPricePackage : undefined
    const villaTotalGuests = villaPackage ? getMaxNonInfantGuestCount(villaPackage.roomRate?.capacities ?? []) : undefined

    const totalGuests = bedbankRoomsList?.[0]?.totalGuests ?? leRoomsList?.[0]?.totalGuests ?? bedbankTotalGuests ?? villaTotalGuests
    const numberOfBedrooms = bedbankRoomsList?.[0]?.numberOfBedrooms ?? leRoomsList?.[0]?.numberOfBedrooms ?? bedbankPackage?.numberOfBedrooms ?? villaPackage?.roomType?.numberOfBedrooms
    const numberOfBathrooms = bedbankRoomsList?.[0]?.numberOfBathrooms ?? leRoomsList?.[0]?.numberOfBathrooms ?? bedbankPackage?.numberOfBathrooms ?? villaPackage?.roomType?.numberOfBathrooms

    if (totalGuests == null || numberOfBedrooms == null || numberOfBathrooms == null) return undefined

    return { numberOfGuests: totalGuests, numberOfBedrooms, numberOfBathrooms }
  }, [isSingleUnit, bedbankRoomsList, leRoomsList, offer])

  const shouldHideRoomBreakdown = useMemo(() =>
    shouldHideSearchTileRoomBreakdown({ offer, filters, isEntireHome: !!entireHome }),
  [offer, filters, entireHome])

  const allRooms = useMemo(() =>
    [...leRoomsList, ...bedbankRoomsList],
  [leRoomsList, bedbankRoomsList])

  const recommendedForText = useMemo(() => {
    const numberOfAdults = filters?.rooms?.reduce((sum, room) => sum + room.adults, 0) ?? 0
    const numberOfChildren = filters?.rooms?.reduce((sum, room) => sum + (room.teenagers ?? 0) + (room.children ?? 0) + (room.infants ?? 0), 0) ?? 0

    // we don't want to concatenate the strings here cuz it breaks RTL languages but can't figure another way of doing this without separating 2 clauses of the sentence
    return intl.formatMessage({
      id: 'hFKgypXkRdFZeyMIfhxs',
      defaultMessage: 'Recommended for {numberOfAdults, plural, one {# adult} other {# adults}}{numberOfChildren, plural, =0 {} one {, # child} other {, # children}}',
      description: 'Room breakdown recommendation showing adult and optional children guest count on search tile',
    }, { numberOfAdults, numberOfChildren })
  }, [filters?.rooms, intl])

  if (shouldHideRoomBreakdown) return null

  if (isBedbankLoading) return <RoomBreakdownLoadingSkeleton />

  if (allRooms.length === 0 && !entireHome) return null

  const isAnytime = isAnytimeSearch(filters)

  return (
    <RoomBreakdownDetails
      recommendedForText={recommendedForText}
      showRecommendation={!isAnytime && metaData?.available !== false}
      rooms={allRooms}
      entireHome={entireHome}
    />
  )
}

export default SearchTileRoomBreakdown
